import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Tabs, Tab, Badge, Table, Form, InputGroup } from 'react-bootstrap';
import { FaCrown, FaTrophy, FaFire, FaMedal, FaSearch, FaShieldAlt, FaAward, FaCalendarAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import useCachedFetch from '../hooks/useCachedFetch';
import SEO from '../components/SEO';

const getFormattedPlayerName = (player) => {
    if (!player) return 'Unknown Player';
    if (player.name) return player.name;
    if (player.firstName || player.lastName) {
        return `${player.firstName || ''} ${player.lastName || ''}`.trim();
    }
    return 'Unknown Player';
};

const Leaderboards = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    // Fetch endpoints
    const { data: repRes, loading: repLoading } = useCachedFetch(`${backendUrl}/api/reputation?limit=30`, { cacheDuration: 60 * 1000 });
    const { data: currentSeasonRes, loading: currentLoading } = useCachedFetch(`${backendUrl}/api/season/current?limit=30`, { cacheDuration: 60 * 1000 });
    const { data: prevSeasonRes, loading: prevLoading } = useCachedFetch(`${backendUrl}/api/season/previous?limit=30`, { cacheDuration: 60 * 1000 });

    const [activeTab, setActiveTab] = useState('reputation');
    const [searchQuery, setSearchQuery] = useState('');

    // Filter Reputation List
    const reputationList = useMemo(() => {
        if (!repRes || !repRes.data) return [];
        return repRes.data.filter(p => 
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.job && p.job.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.community && p.community.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [repRes, searchQuery]);

    // Current Season Data
    const currentSeasonMeta = currentSeasonRes?.season || null;

    const filteredCurrentSeasonData = useMemo(() => {
        const data = currentSeasonRes?.data || [];
        return data.filter(p => 
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (p.job && p.job.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    }, [currentSeasonRes, searchQuery]);

    // Previous Season Data
    const prevSeasonData = prevSeasonRes?.data || [];
    const prevSeasonMeta = prevSeasonRes?.season || null;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    const getPodiumBorder = (rank) => {
        if (rank === 1) return 'var(--neon-gold)';
        if (rank === 2) return '#e2e8f0';
        if (rank === 3) return '#cd7f32';
        return 'rgba(255,255,255,0.1)';
    };

    const getPodiumGlow = (rank) => {
        if (rank === 1) return '0 0 25px rgba(255, 183, 0, 0.4)';
        if (rank === 2) return '0 0 20px rgba(226, 232, 240, 0.3)';
        if (rank === 3) return '0 0 15px rgba(205, 127, 50, 0.3)';
        return 'none';
    };

    return (
        <div style={{ minHeight: '100vh', paddingTop: '60px' }}>
            <SEO
                title="Citizen Leaderboards & Seasonal Standings"
                description="View official citizen XP rankings, lifetime reputation, seasonal tournament champions, and hall of fame records for Thalai Nagaram RP (TNRP)."
                keywords="TNRP Leaderboards, Thalai Nagaram RP Rankings, Tamil FiveM Player Stats, TNRP Season Tournament, FiveM Top Players Tamil"
                canonicalUrl="https://thalainagaram.org/leaderboards"
            />
            {/* Ambient Atmosphere */}
            <div style={{ position: 'fixed', top: '15%', right: '-10%', width: '50vw', height: '50vw', background: 'radial-gradient(circle, rgba(255, 183, 0, 0.08) 0%, transparent 60%)', filter: 'blur(90px)', zIndex: -1 }} />
            <div style={{ position: 'fixed', bottom: '-10%', left: '-10%', width: '45vw', height: '45vw', background: 'radial-gradient(circle, rgba(0, 229, 255, 0.08) 0%, transparent 60%)', filter: 'blur(90px)', zIndex: -1 }} />

            <Container fluid className="py-5">
                <Row className="justify-content-center">
                    <Col lg={11} xl={10}>
                        {/* Title Header */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center mb-5"
                        >
                            <div className="d-inline-block px-3 py-1 mb-3 border border-warning border-opacity-50 text-neon-gold tracking-widest small fw-bold" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                OFFICIAL LEADERBOARDS
                            </div>
                            <h1 className="display-4 fw-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif', textShadow: '0 0 25px rgba(255, 183, 0, 0.4)', fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
                                CITIZEN RANKINGS & <span className="text-neon-cyan">SEASONS</span>
                            </h1>
                            <p className="lead text-secondary mx-auto mb-4" style={{ maxWidth: '650px' }}>
                                Lifetime XP standing, seasonal performance, and hall of fame achievements of Thalainagaram.
                            </p>

                            {/* Total Players Counter */}
                            {repRes && (
                                <motion.div 
                                    initial={{ scale: 0.9, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="d-inline-flex align-items-center gap-3 px-4 py-2 rounded-pill bg-dark bg-opacity-75 border border-cyan border-opacity-40"
                                    style={{ boxShadow: '0 0 20px rgba(0, 229, 255, 0.2)' }}
                                >
                                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#00ff88', boxShadow: '0 0 10px #00ff88' }} />
                                    <span className="text-white fw-bold font-monospace" style={{ letterSpacing: '1px' }}>
                                        TOTAL REGISTERED PLAYERS: <strong className="text-neon-gold fs-5">{(repRes.totalPlayers || reputationList.length).toLocaleString()}</strong>
                                    </span>
                                </motion.div>
                            )}

                            <div style={{ width: '100px', height: '2px', background: 'var(--neon-gold)', margin: '2rem auto 0', boxShadow: '0 0 10px var(--neon-gold)' }} />
                        </motion.div>

                        {/* Search Input */}
                        <div className="mb-4 d-flex justify-content-end">
                            <InputGroup style={{ maxWidth: '350px' }}>
                                <InputGroup.Text bg="dark" className="bg-dark border-secondary text-secondary">
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search player name or job..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="bg-dark text-white border-secondary shadow-none"
                                />
                            </InputGroup>
                        </div>

                        {/* Leaderboard Tabs */}
                        <style>
                            {`
                                .custom-tabs .nav-link { color: rgba(255,255,255,0.6); font-family: 'Orbitron', sans-serif; border: none; padding: 1rem 1.5rem; font-size: 1rem; }
                                .custom-tabs .nav-link.active { background: transparent; color: var(--neon-gold); border-bottom: 3px solid var(--neon-gold); }
                                .custom-tabs { border-bottom: 1px solid rgba(255,255,255,0.1); }
                            `}
                        </style>

                        <Tabs
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            className="mb-5 custom-tabs justify-content-center"
                        >
                            {/* TAB 1: REPUTATION */}
                            <Tab eventKey="reputation" title={<span><FaCrown className="me-2 text-warning" /> REPUTATION (LIFETIME XP)</span>}>
                                {repLoading ? (
                                    <div className="text-center py-5 text-secondary">Loading reputation rankings...</div>
                                ) : reputationList.length === 0 ? (
                                    <div className="text-center py-5 text-secondary">No player records found.</div>
                                ) : (
                                    <>
                                        {/* Top 3 Podium */}
                                        <Row className="g-4 mb-5 justify-content-center align-items-end">
                                            {reputationList.slice(0, 3).map((player) => (
                                                <Col key={player.rank} lg={4} md={6} className={player.rank === 1 ? 'order-1 order-lg-2 mb-4 mb-lg-0' : player.rank === 2 ? 'order-2 order-lg-1' : 'order-3'}>
                                                    <motion.div
                                                        whileHover={{ scale: 1.03 }}
                                                        className="p-4 text-center rounded position-relative overflow-hidden"
                                                        style={{
                                                            background: 'var(--bg-card)',
                                                            border: `2px solid ${getPodiumBorder(player.rank)}`,
                                                            boxShadow: getPodiumGlow(player.rank),
                                                            transform: player.rank === 1 ? 'scale(1.05)' : 'none'
                                                        }}
                                                    >
                                                        <div className="mb-3">
                                                            {player.rank === 1 ? (
                                                                <FaCrown size={42} color="var(--neon-gold)" style={{ filter: 'drop-shadow(0 0 10px rgba(255, 183, 0, 0.8))' }} />
                                                            ) : player.rank === 2 ? (
                                                                <FaShieldAlt size={36} color="#e2e8f0" />
                                                            ) : (
                                                                <FaMedal size={36} color="#cd7f32" />
                                                            )}
                                                        </div>
                                                        <Badge bg="transparent" className="border px-3 py-1 mb-2 fw-bold" style={{ borderColor: getPodiumBorder(player.rank), color: getPodiumBorder(player.rank) }}>
                                                            RANK #{player.rank}
                                                        </Badge>
                                                        <h3 className="h4 fw-bold text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>{getFormattedPlayerName(player)}</h3>
                                                        <p className="text-secondary small mb-3">{player.community && player.community !== 'null' ? player.community : 'Not in community'}</p>

                                                        <div className="p-3 bg-dark bg-opacity-60 rounded border border-secondary border-opacity-30 mb-3">
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span className="text-secondary small">TOTAL XP:</span>
                                                                <span className="text-neon-gold fw-bold font-monospace fs-5">{player.xp.toLocaleString()} XP</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between mb-1">
                                                                <span className="text-secondary small">LEVEL:</span>
                                                                <span className="text-neon-cyan fw-bold">LVL {player.level}</span>
                                                            </div>
                                                            <div className="d-flex justify-content-between">
                                                                <span className="text-secondary small">JOB ROLE:</span>
                                                                <span className="text-success fw-bold small">{player.job && player.job !== 'null' ? player.job : 'Not in job'}</span>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                </Col>
                                            ))}
                                        </Row>

                                        {/* Reputation Table */}
                                        <div className="p-4 rounded border border-secondary border-opacity-30 bg-dark bg-opacity-75 shadow-lg">
                                            <Table responsive hover variant="dark" className="align-middle mb-0">
                                                <thead>
                                                    <tr className="text-secondary border-secondary">
                                                        <th>RANK</th>
                                                        <th>PLAYER NAME</th>
                                                        <th>LEVEL</th>
                                                        <th>TOTAL XP</th>
                                                        <th>JOB ROLE</th>
                                                        <th>COMMUNITY</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reputationList.map((player) => (
                                                        <tr key={player.rank} className="border-secondary">
                                                            <td className="fw-bold font-monospace">
                                                                <span style={{ color: getPodiumBorder(player.rank) }}>#{player.rank}</span>
                                                            </td>
                                                            <td className="fw-bold text-white">{getFormattedPlayerName(player)}</td>
                                                            <td><span className="text-neon-cyan fw-bold">LVL {player.level}</span></td>
                                                            <td className="fw-bold font-monospace text-neon-gold fs-6">{player.xp.toLocaleString()} XP</td>
                                                            <td className="text-success font-monospace small">{player.job && player.job !== 'null' ? player.job : 'Not in job'}</td>
                                                            <td className="text-secondary small">{player.community && player.community !== 'null' ? player.community : 'Not in community'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                )}
                            </Tab>

                            {/* TAB 2: CURRENT SEASON */}
                            <Tab eventKey="season" title={<span><FaFire className="me-2 text-danger" /> CURRENT SEASON</span>}>
                                {currentLoading ? (
                                    <div className="text-center py-5 text-secondary">Loading current season leaderboard...</div>
                                ) : !currentSeasonMeta ? (
                                    <div className="text-center py-5 px-4 rounded border border-secondary border-opacity-30 bg-dark bg-opacity-60 my-4">
                                        <FaFire size={40} className="text-secondary mb-3 opacity-50" />
                                        <h3 className="h5 text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>NO ACTIVE SEASON AT PRESENT</h3>
                                        <p className="text-secondary mb-0">Check back soon for the announcement of our next seasonal tournament!</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Season Meta Card */}
                                        <div className="p-4 rounded border border-danger border-opacity-40 bg-dark bg-opacity-80 mb-4 text-center">
                                            <Badge bg="danger" className="px-3 py-2 text-uppercase mb-2" style={{ letterSpacing: '1px' }}>
                                                {currentSeasonMeta.status === 'active' ? '● LIVE SEASON' : currentSeasonMeta.status}
                                            </Badge>
                                            <h2 className="h3 fw-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                {currentSeasonMeta.label}
                                            </h2>
                                            <div className="d-flex align-items-center justify-content-center gap-2 text-secondary small">
                                                <FaCalendarAlt />
                                                <span>{formatDate(currentSeasonMeta.startsAt)} — {formatDate(currentSeasonMeta.endsAt)}</span>
                                            </div>
                                        </div>

                                        {/* Current Season Table */}
                                        <div className="p-4 rounded border border-secondary border-opacity-30 bg-dark bg-opacity-75 shadow-lg">
                                            <Table responsive hover variant="dark" className="align-middle mb-0">
                                                <thead>
                                                    <tr className="text-secondary border-secondary">
                                                        <th>RANK</th>
                                                        <th>PLAYER NAME</th>
                                                        <th>LEVEL</th>
                                                        <th>SEASON XP</th>
                                                        <th>JOB ROLE</th>
                                                        <th>COMMUNITY</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredCurrentSeasonData.map((player) => (
                                                        <tr key={player.rank} className="border-secondary">
                                                            <td className="fw-bold font-monospace text-neon-gold">#{player.rank}</td>
                                                            <td className="fw-bold text-white">{getFormattedPlayerName(player)}</td>
                                                            <td><span className="text-neon-cyan fw-bold">LVL {player.level}</span></td>
                                                            <td className="fw-bold font-monospace text-danger fs-6">{player.xp.toLocaleString()} XP</td>
                                                            <td className="text-success font-monospace small">{player.job && player.job !== 'null' ? player.job : 'Not in job'}</td>
                                                            <td className="text-secondary small">{player.community && player.community !== 'null' ? player.community : 'Not in community'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                )}
                            </Tab>

                            {/* TAB 3: PREVIOUS SEASON */}
                            <Tab eventKey="previous" title={<span><FaTrophy className="me-2 text-primary" /> PREVIOUS SEASON</span>}>
                                {prevLoading ? (
                                    <div className="text-center py-5 text-secondary">Loading previous season records...</div>
                                ) : !prevSeasonMeta || prevSeasonData.length === 0 ? (
                                    <div className="text-center py-5 px-4 rounded border border-secondary border-opacity-30 bg-dark bg-opacity-60 my-4">
                                        <FaAward size={40} className="text-secondary mb-3 opacity-50" />
                                        <h3 className="h5 text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>NO PREVIOUS SEASON HAS ENDED YET</h3>
                                        <p className="text-secondary mb-0">Previous season champions and standings will automatically be recorded here after Season 1 concludes.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="p-4 rounded border border-warning border-opacity-40 bg-dark bg-opacity-80 mb-4 text-center">
                                            <Badge bg="warning" text="dark" className="px-3 py-2 text-uppercase mb-2">
                                                ENDED SEASON CHAMPIONS
                                            </Badge>
                                            <h2 className="h3 fw-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                {prevSeasonMeta.label}
                                            </h2>
                                            <div className="d-flex align-items-center justify-content-center gap-2 text-secondary small">
                                                <FaCalendarAlt />
                                                <span>{formatDate(prevSeasonMeta.startsAt)} — {formatDate(prevSeasonMeta.endsAt)}</span>
                                            </div>
                                        </div>

                                        <div className="p-4 rounded border border-secondary border-opacity-30 bg-dark bg-opacity-75 shadow-lg">
                                            <Table responsive hover variant="dark" className="align-middle mb-0">
                                                <thead>
                                                    <tr className="text-secondary border-secondary">
                                                        <th>RANK</th>
                                                        <th>PLAYER NAME</th>
                                                        <th>LEVEL</th>
                                                        <th>FINAL SEASON XP</th>
                                                        <th>JOB ROLE</th>
                                                        <th>COMMUNITY</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {prevSeasonData.map((player) => (
                                                        <tr key={player.rank} className="border-secondary">
                                                            <td className="fw-bold font-monospace text-neon-gold">#{player.rank}</td>
                                                            <td className="fw-bold text-white">{getFormattedPlayerName(player)}</td>
                                                            <td><span className="text-neon-cyan fw-bold">LVL {player.level}</span></td>
                                                            <td className="fw-bold font-monospace text-warning fs-6">{player.xp.toLocaleString()} XP</td>
                                                            <td className="text-success font-monospace small">{player.job && player.job !== 'null' ? player.job : 'Not in job'}</td>
                                                            <td className="text-secondary small">{player.community && player.community !== 'null' ? player.community : 'Not in community'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </>
                                )}
                            </Tab>
                        </Tabs>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Leaderboards;
